import React, { useState } from 'react';
import { 
  getAIAnalysis, 
  getAIAdvice, 
  getAIHabitsPlan, 
  getAIHealthAnswer,
  AIAnalysisResponse,
  AIAdviceResponse,
  AIHabitsPlanResponse,
  AIHealthQuestionResponse
} from '../lib/api';

interface AIScreenProps {
  onBack: () => void;
}

export const AIScreen: React.FC<AIScreenProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'analysis' | 'advice' | 'habits' | 'question'>('analysis');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');

  const handleAIAnalysis = async () => {
    setLoading(true);
    try {
      const response: AIAnalysisResponse = await getAIAnalysis();
      setResult(
        `📊 Анализ:\n${response.analysis}\n\n` +
        `💡 Инсайты:\n${response.insights.map(i => `• ${i}`).join('\n')}\n\n` +
        `🎯 Рекомендации:\n${response.recommendations.map(r => `• ${r}`).join('\n')}`
      );
    } catch (error) {
      setResult('❌ Ошибка при получении анализа');
    } finally {
      setLoading(false);
    }
  };

  const handleAIAdvice = async () => {
    if (!context.trim()) {
      setResult('❌ Введите контекст для совета');
      return;
    }
    
    setLoading(true);
    try {
      const response: AIAdviceResponse = await getAIAdvice(context);
      setResult(`🦊 Совет:\n${response.advice}`);
    } catch (error) {
      setResult('❌ Ошибка при получении совета');
    } finally {
      setLoading(false);
    }
  };

  const handleAIHabitsPlan = async () => {
    setLoading(true);
    try {
      const response: AIHabitsPlanResponse = await getAIHabitsPlan();
      setResult(
        `📋 План привычек (${response.duration}, сложность: ${response.difficulty}):\n\n` +
        `${response.plan}\n\n` +
        `🎯 Ключевые привычки:\n${response.habits.map(h => `• ${h}`).join('\n')}`
      );
    } catch (error) {
      setResult('❌ Ошибка при создании плана привычек');
    } finally {
      setLoading(false);
    }
  };

  const handleAIHealthQuestion = async () => {
    if (!question.trim()) {
      setResult('❌ Введите вопрос');
      return;
    }
    
    setLoading(true);
    try {
      const response: AIHealthQuestionResponse = await getAIHealthAnswer(question);
      setResult(`❓ Вопрос: ${question}\n\n🦊 Ответ:\n${response.answer}`);
    } catch (error) {
      setResult('❌ Ошибка при получении ответа');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analysis':
        return (
          <div className="ai-tab-content">
            <p className="ai-description">
              Получите AI-анализ ваших данных за неделю с персональными рекомендациями
            </p>
            <button 
              className="ai-button primary"
              onClick={handleAIAnalysis}
              disabled={loading}
            >
              {loading ? '🔄 Анализируем...' : '📊 Получить анализ'}
            </button>
          </div>
        );
      
      case 'advice':
        return (
          <div className="ai-tab-content">
            <p className="ai-description">
              Получите персональный совет по любому аспекту здоровья
            </p>
            <input
              type="text"
              placeholder="Например: улучшение сна, больше активности..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="ai-input"
            />
            <button 
              className="ai-button primary"
              onClick={handleAIAdvice}
              disabled={loading}
            >
              {loading ? '🔄 Получаем совет...' : '🦊 Получить совет'}
            </button>
          </div>
        );
      
      case 'habits':
        return (
          <div className="ai-tab-content">
            <p className="ai-description">
              Создайте персональный 30-дневный план привычек
            </p>
            <button 
              className="ai-button primary"
              onClick={handleAIHabitsPlan}
              disabled={loading}
            >
              {loading ? '🔄 Создаем план...' : '📋 Создать план привычек'}
            </button>
          </div>
        );
      
      case 'question':
        return (
          <div className="ai-tab-content">
            <p className="ai-description">
              Задайте любой вопрос о здоровье и получите ответ
            </p>
            <textarea
              placeholder="Ваш вопрос о здоровье..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="ai-textarea"
              rows={3}
            />
            <button 
              className="ai-button primary"
              onClick={handleAIHealthQuestion}
              disabled={loading}
            >
              {loading ? '🔄 Получаем ответ...' : '❓ Задать вопрос'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="screen">
      <div className="header">
        <button className="back-button" onClick={onBack}>
          ← Назад
        </button>
        <h1>🧠 AI Ассистент</h1>
      </div>

      <div className="ai-tabs">
        <button 
          className={`ai-tab ${activeTab === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          📊 Анализ
        </button>
        <button 
          className={`ai-tab ${activeTab === 'advice' ? 'active' : ''}`}
          onClick={() => setActiveTab('advice')}
        >
          🦊 Совет
        </button>
        <button 
          className={`ai-tab ${activeTab === 'habits' ? 'active' : ''}`}
          onClick={() => setActiveTab('habits')}
        >
          📋 План
        </button>
        <button 
          className={`ai-tab ${activeTab === 'question' ? 'active' : ''}`}
          onClick={() => setActiveTab('question')}
        >
          ❓ Вопрос
        </button>
      </div>

      {renderTabContent()}

      {result && (
        <div className="ai-result">
          <h3>Результат:</h3>
          <div className="ai-result-content">
            {result.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
